/**
 * Plan Form — create or edit an expedition plan.
 *
 * Sections:
 * - Basic Information (name, location, description)
 * - Timeline (flexible time list: meeting, start, turnaround, return, overdue, custom)
 * - Decision Framework (factors with green/yellow/red, rules, hazards)
 * - Pack List (team gear, individual gear as free-text)
 */

import { logger } from '../../utils/logger.js'
import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { planStorage } from '../../storage/plan-storage.js'
import { overpassToGeoJSON } from '../../lib/overpass-geometry.js'
import { db } from '../../database/schema.js'
import { router } from '../../utils/router.js'
import { forcePushEntity, syncListOnNavigate } from '../../storage/sync-manager.js'
import { featureStorage } from '../../storage/feature-storage.js'
import type { Feature } from '../../schemas/feature.js'
import type {
  Plan,
  PlanTime,
  PlanTimeTag,
  PlanLocation,
  DecisionFactor,
  PlanPackItem,
  PlanEmergencyContact,
  RichText,
} from '../../schemas/plan.js'
import type { JSONContent } from '../../lib/tiptap-config.js'
import type { RichTextContentEvent } from '../ui/rich-text-editor.js'
import '../location-search/location-search.js'
import './trip-map-preview.js'
import './plan-pack-editor.js'

import { authService } from '../../services/auth/auth-service.js'
import type { Team } from '../../schemas/team.js'
import type { User } from '../../schemas/user.js'
import { edgeService, nodeRef } from '../../services/edge-service.js'

const log = logger.create('plan-form')

// Material Web Components
import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/button/text-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/select/outlined-select.js'
import '@material/web/select/select-option.js'
import '@material/web/iconbutton/icon-button.js'

/** Well-known time tags with default names and icons */
const TIME_TAG_DEFAULTS: { tag: PlanTimeTag; name: string; icon: string }[] = [
  { tag: 'meeting', name: 'Meeting Time', icon: 'group' },
  { tag: 'start', name: 'Start Time', icon: 'play_arrow' },
  { tag: 'turnaround', name: 'Turnaround Time', icon: 'u_turn_left' },
  { tag: 'return', name: 'Expected Return', icon: 'home' },
  { tag: 'overdue', name: 'Overdue By', icon: 'warning' },
]

/** Default decision factor options per the design doc */
/** Default decision factors with pre-populated thresholds */
const DEFAULT_FACTORS: Omit<DecisionFactor, 'id'>[] = [
  {
    name: 'Wind',
    whenWillWeKnow: 'continuous',
    green: 'None to Some',
    yellow: 'Unpleasant',
    red: 'Knock over',
  },
  {
    name: 'Precipitation',
    whenWillWeKnow: 'continuous',
    green: 'None',
    yellow: 'Flurries',
    red: 'Storm',
  },
  {
    name: 'Trail Conditions',
    whenWillWeKnow: 'continuous',
    green: 'Firm',
    yellow: 'Some Postholing',
    red: 'Need Snowshoes',
  },
  {
    name: 'Timing',
    whenWillWeKnow: 'continuous',
    green: 'On Time',
    yellow: 'An hour behind',
    red: '90 minutes behind',
  },
  { name: 'Avalanche', whenWillWeKnow: 'continuous', green: 'None', yellow: 'None', red: 'Some' },
  {
    name: 'Feeling',
    whenWillWeKnow: 'continuous',
    green: 'Great',
    yellow: "I'm willing",
    red: "I don't want to do this",
  },
]

const DEFAULT_RULES = ['', '3 Yellow = No Go!', '1 Red = No Go!']

@customElement('plan-form')
export class PlanForm extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      max-height: 100%;
      background: var(--cairn-background-color, #f8fafc);
    }

    .form-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: scroll;
    }

    @media (max-width: 769px) {
      .form-container {
        height: calc(100% - 60px);
      }
    }

    .form-header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: var(--cairn-spacing-md, 16px);
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--cairn-spacing-md, 16px);
      flex-shrink: 0;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
    }

    .back-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-primary-color, #2d3748);
    }

    .back-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .form-title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .form-content {
      flex: 1;
      padding: var(--cairn-spacing-lg, 24px);
      overflow-y: auto;
    }

    .form-fields {
      max-width: 900px;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-lg, 24px);
    }

    .form-section {
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-md, 16px);
    }

    .basic-info-layout {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: var(--cairn-spacing-lg, 24px);
      align-items: stretch;
    }

    .basic-info-fields {
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-md, 16px);
    }

    .basic-info-map {
      border-radius: var(--cairn-border-radius, 8px);
      overflow: hidden;
      height: 250px;
      position: relative;
    }

    .basic-info-map trip-map-preview {
      height: 100%;
      border-radius: var(--cairn-border-radius, 8px);
      position: relative;
      z-index: 0;
    }

    .map-edit-overlay {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 8px;
      background: rgba(0, 0, 0, 0.5);
      color: white;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
    }

    .map-edit-overlay .material-icons {
      font-size: 16px;
    }

    .map-edit-overlay.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    @media (max-width: 640px) {
      .basic-info-layout {
        grid-template-columns: 1fr;
      }

      .basic-info-map {
        height: 200px;
      }
    }

    .section-title {
      font-size: var(--cairn-text-size-lg, 20px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding-bottom: var(--cairn-spacing-sm, 8px);
    }

    .maps-link {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      color: var(--cairn-primary-color, #3b82f6);
      text-decoration: none;
      margin-top: -8px;
    }

    .maps-link:hover {
      text-decoration: underline;
    }

    .maps-link .material-icons {
      font-size: 14px;
    }

    .section-description {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin: -8px 0 0 0;
    }

    .form-row {
      display: flex;
      gap: var(--cairn-spacing-md, 16px);
    }

    .form-row > * {
      flex: 1;
    }

    location-search {
      --location-search-border-radius: var(--md-outlined-text-field-container-shape, 4px);
    }

    .textarea-field {
      min-height: 120px;
    }

    .textarea-field-sm {
      min-height: 80px;
    }

    /* Connected time entries */
    .time-entries-group {
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      overflow: hidden;
    }

    .time-entry-wrapper {
      position: relative;
      background: var(--cairn-surface-color, #ffffff);
      transition:
        border-color 0.15s,
        opacity 0.15s;
    }

    .time-entry-wrapper + .time-entry-wrapper {
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .time-entry-wrapper.drag-over {
      border-color: var(--cairn-primary-color, #3b82f6);
      border-style: dashed;
    }

    .time-entry-wrapper.dragging {
      cursor: grabbing;
      opacity: 0.7;
    }

    .time-entry-controls {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 16px);
    }

    .time-entry-actions {
      display: flex;
      flex-shrink: 0;
      gap: 0;
    }

    .time-entry-grip {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
    }

    @media (max-width: 768px) {
      .time-entry-grip {
        flex-direction: column;
        gap: 0;
      }

      .time-entry-actions {
        flex-direction: column;
      }
    }

    .drag-handle {
      font-size: 20px;
      color: var(--cairn-text-secondary-color, #64748b);
      flex-shrink: 0;
      cursor: grab;
      opacity: 0.5;
    }

    .drag-handle:hover {
      opacity: 1;
    }

    .time-icon {
      font-size: 20px;
      color: var(--cairn-text-secondary-color, #64748b);
      flex-shrink: 0;
    }

    .time-fields {
      flex: 1;
      min-width: 0;
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      align-items: center;
    }

    .time-fields md-outlined-text-field {
      flex: 1;
      min-width: 0;
    }

    .time-fields md-outlined-text-field[type='datetime-local'] {
      flex: 1;
      min-width: 0;
    }

    .location-button {
      flex-shrink: 0;
      --md-icon-button-icon-color: var(--cairn-text-secondary-color, #64748b);
    }

    .time-location-row {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 0 var(--cairn-spacing-md, 16px) var(--cairn-spacing-sm, 8px) 40px;
    }

    .time-location-search {
      padding: 0 var(--cairn-spacing-md, 16px) var(--cairn-spacing-sm, 8px) 40px;
    }

    .route-point-chips {
      display: flex;
      gap: 6px;
      overflow-x: auto;
      padding: 6px 0 2px 0;
      scrollbar-width: none;
    }

    .route-point-chips::-webkit-scrollbar {
      display: none;
    }

    .route-point-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border-radius: 16px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-color, #1e293b);
      cursor: pointer;
      white-space: nowrap;
    }

    .route-point-chip .material-icons {
      font-size: 14px;
    }

    .time-note-row {
      display: flex;
      align-items: flex-start;
      gap: 4px;
      padding: 0 var(--cairn-spacing-md, 16px) var(--cairn-spacing-sm, 8px) 40px;
    }

    .time-note-preview {
      flex: 1;
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
      white-space: pre-wrap;
      line-height: 1.4;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .time-note-edit {
      padding: 0 var(--cairn-spacing-md, 16px) var(--cairn-spacing-sm, 8px) 40px;
    }

    .time-note-edit md-outlined-text-field {
      width: 100%;
    }

    /* Decision table */
    .decision-table-wrapper {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
    }

    .decision-table {
      width: 100%;
      min-width: 600px;
      border-collapse: collapse;
      table-layout: fixed;
    }

    .decision-table th,
    .decision-table td {
      padding: 8px 12px;
      text-align: left;
      vertical-align: top;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .decision-table th {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: white;
      position: sticky;
      top: 0;
    }

    .decision-table th.col-drag {
      width: 24px;
      padding: 8px 4px;
      background: var(--cairn-surface-color, #fff);
    }
    .decision-table th.col-factor {
      width: 150px;
      background: var(--cairn-surface-color, #fff);
      color: var(--cairn-text-color, #1e293b);
    }
    .decision-table th.col-green {
      background: #166534;
    }
    .decision-table th.col-yellow {
      background: #92400e;
    }
    .decision-table th.col-red {
      background: #991b1b;
    }

    .decision-table td:first-child {
      padding: 8px 4px;
      vertical-align: middle;
    }

    .decision-table td.cell-green {
      background: #f0fdf4;
      color: #14532d;
    }
    .decision-table td.cell-yellow {
      background: #fffbeb;
      color: #78350f;
    }
    .decision-table td.cell-red {
      background: #fef2f2;
      color: #7f1d1d;
    }

    .decision-table tr:last-child td {
      border-bottom: none;
    }

    .decision-table tr.drag-over td {
      border-bottom: 2px solid var(--cairn-primary-color, #3b82f6);
    }

    .decision-table td .drag-handle {
      font-size: 18px;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: grab;
      opacity: 0.4;
    }

    .decision-table td .drag-handle:hover {
      opacity: 1;
    }

    .decision-table .drag-handle[draggable='true'] {
      cursor: grab;
    }
    .decision-table .drag-handle[draggable='true']:active {
      cursor: grabbing;
    }

    .decision-table .factor-cell {
      display: flex;
      align-items: flex-start;
      gap: 2px;
    }

    .decision-table .factor-cell .cell-input {
      flex: 1;
      min-width: 0;
    }

    .decision-table .factor-cell md-icon-button {
      --md-icon-button-icon-size: 16px;
      --md-icon-button-state-layer-height: 28px;
      --md-icon-button-state-layer-width: 28px;
      height: 28px;
      width: 28px;
      flex-shrink: 0;
    }

    .decision-table .factor-cell .material-icons {
      font-size: 16px;
    }

    .decision-table .cell-input {
      width: 100%;
      border: none;
      background: transparent;
      font-size: 14px;
      font-family: inherit;
      color: inherit;
      padding: 4px 2px;
      outline: none;
      border-radius: 4px;
    }

    .decision-table .cell-input:focus {
      background: rgba(0, 0, 0, 0.04);
    }

    .decision-table .cell-input::placeholder {
      color: var(--cairn-text-secondary-color, #94a3b8);
    }

    .decision-table tr.rules-row td {
      border-top: 2px solid var(--cairn-border-color, #e2e8f0);
      font-weight: 600;
    }

    .decision-table tr.rules-row td.add-factor-cell {
      text-align: center;
      padding: 4px 0;
    }

    .decision-table tr.rules-row .cell-input {
      font-weight: 600;
    }

    /* Add button row */
    .add-row {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      flex-wrap: wrap;
    }

    .form-actions {
      padding: var(--cairn-spacing-xl, 24px) var(--cairn-spacing-md, 16px);
      display: flex;
      gap: var(--cairn-spacing-md, 16px);
      justify-content: flex-end;
    }

    /* Material Icons styling */
    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      user-select: none;
    }

    /* Hide header on desktop - title shown in main app header */
    @media (min-width: 769px) {
      .form-header {
        display: none;
      }
    }

    /* Desktop: show buttons, hide dropdown */
    .add-times-mobile {
      display: none;
    }

    @media (max-width: 640px) {
      /* Mobile: hide buttons, show dropdown */
      .add-times-desktop {
        display: none;
      }
      .add-times-mobile {
        display: block;
      }

      .form-row {
        flex-direction: column;
        grid-template-columns: 1fr;
      }

      .time-fields {
        flex-direction: column;
        align-items: stretch;
      }
    }
  `

  @property({ type: String })
  editPlanId?: string

  @property({ type: String })
  sourceRouteId?: string

  @state() private existingPlan: Plan | null = null

  // Basic
  @state() private name = ''
  @state() private description = ''
  @state() private descriptionRich: JSONContent | undefined = undefined
  @state() private location: PlanLocation | null = null
  private locationOsmType: string | undefined
  private locationOsmId: number | undefined
  private locationIcon: string | undefined
  private locationChanged = false

  // Times
  @state() private times: PlanTime[] = []
  private dragIndex: number | null = null
  @state() private dragOverIndex: number | null = null
  private factorDragIndex: number | null = null
  @state() private factorDragOverIndex: number | null = null

  // Decision Framework
  @state() private decisionFactors: DecisionFactor[] = []
  @state() private decisionRules: string[] = []
  @state() private objectiveHazards = ''
  @state() private objectiveHazardsRich: JSONContent | undefined = undefined

  // Pack List
  @state() private emergencyContacts: PlanEmergencyContact[] = []
  @state() private availableContacts: Array<{
    label: string
    sublabel: string
    contact: PlanEmergencyContact
  }> = []
  @state() private teamGear: PlanPackItem[] = []
  @state() private individualGear: PlanPackItem[] = []

  // Members
  @state() private planMembers: Array<{
    userId: string
    displayName: string
    picture?: string
    role: string
  }> = []
  @state() private knownUsers: User[] = []
  @state() private knownTeams: Array<{
    team: Team
    members: User[]
    memberUserIds: string[]
    totalMembers: number
  }> = []
  @state() private memberInput = ''
  @state() private showMemberSuggestions = false

  // Plan features for map preview
  @state() private planFeatures: Feature[] = []

  private get planPointFeatures(): Feature[] {
    return this.planFeatures.filter(f => f.type === 'point')
  }

  private async loadMaterialIcons(): Promise<void> {
    try {
      const materialStyle = document.createElement('style')
      materialStyle.textContent = `
        @font-face {
          font-family: 'Material Symbols Outlined';
          font-style: normal;
          font-weight: 400;
          src: url('/node_modules/@material-symbols/font-400/material-symbols-outlined.woff2') format('woff2');
        }
        .material-icons {
          font-family: 'Material Symbols Outlined';
          font-weight: normal;
          font-style: normal;
          font-size: 24px;
          line-height: 1;
          letter-spacing: normal;
          text-transform: none;
          display: inline-block;
          white-space: nowrap;
          word-wrap: normal;
          direction: ltr;
          -webkit-font-feature-settings: 'liga';
          -webkit-font-smoothing: antialiased;
        }
      `
      this.shadowRoot?.appendChild(materialStyle)
    } catch (error) {
      log.error('Failed to load Material Symbols CSS:', error)
    }
  }

  override connectedCallback(): void {
    super.connectedCallback()
    void import('../ui/rich-text-editor.js')
    void this.loadMaterialIcons()
    void this.loadPlanIfNeeded()
    void this.loadAvailableContacts()
    void this.loadKnownUsersAndTeams()
  }

  override updated(changedProperties: Map<string, unknown>): void {
    super.updated(changedProperties)
    if (changedProperties.has('editPlanId')) {
      void this.loadPlanIfNeeded()
    }
  }

  private async loadPlanIfNeeded(): Promise<void> {
    if (this.editPlanId) {
      try {
        const plan = await db.plans.get(this.editPlanId)
        if (plan) {
          this.existingPlan = plan
          this.populateFromPlan(plan)
          void this.loadPlanFeatures(this.editPlanId)
          void this.loadPlanMembers()
        }
      } catch (error) {
        log.error('Failed to load plan for editing:', error)
      }
    } else {
      this.existingPlan = null
      this.planFeatures = []
      this.resetForm()
      if (this.sourceRouteId) {
        void this.prefillFromRoute(this.sourceRouteId)
      }
    }
  }

  private async loadPlanFeatures(planId: string): Promise<void> {
    try {
      this.planFeatures = await featureStorage.getFeaturesForEntity('plan', planId)
    } catch (error) {
      log.error('Failed to load plan features:', error)
    }
  }

  private async loadAvailableContacts(): Promise<void> {
    try {
      const userId = authService.getState().userId
      const contacts: typeof this.availableContacts = []

      const teamMembers = await db.teamMembers.toArray()
      const myTeamIds = new Set(
        teamMembers
          .filter(tm => tm.userId === userId && tm.status === 'active')
          .map(tm => tm.teamId)
      )
      const teams = (await db.teams.toArray()).filter(
        (t: Team) => myTeamIds.has(t.id) && t.emergencyContact
      )
      for (const team of teams) {
        const ec = team.emergencyContact!
        contacts.push({
          label: ec.name,
          sublabel: `Team: ${team.name}`,
          contact: {
            id: crypto.randomUUID(),
            name: ec.name,
            phone: ec.phone,
            source: { type: 'team', teamId: team.id, teamName: team.name },
          },
        })
      }

      if (this.editPlanId) {
        const edges = await edgeService.getBySubject(nodeRef('plan', this.editPlanId), 'has-member')
        for (const edge of edges) {
          const memberUserId = edge.object.replace('user:', '')
          const user = await db.users.get(memberUserId)
          if (user?.emergencyContact) {
            const ec = user.emergencyContact
            contacts.push({
              label: ec.name,
              sublabel: `${user.displayName}'s contact`,
              contact: {
                id: crypto.randomUUID(),
                name: ec.name,
                phone: ec.phone,
                relationship: ec.relationship,
                source: {
                  type: 'member',
                  userId: user.id,
                  displayName: user.displayName,
                },
              },
            })
          }
        }
      }

      this.availableContacts = contacts
    } catch {
      // non-critical
    }
  }

  private async loadPlanMembers(): Promise<void> {
    if (!this.editPlanId) return
    try {
      const edges = await edgeService.getBySubject(nodeRef('plan', this.editPlanId), 'has-member')
      const auth = authService.getState()
      const seen = new Set<string>()
      const members = (
        await Promise.all(
          edges.map(async edge => {
            const userId = edge.object.split(':')[1]
            if (seen.has(userId)) return null
            seen.add(userId)
            const props = (edge.properties ?? {}) as Record<string, unknown>
            const user = await db.users.get(userId)
            const isCurrentUser = userId === auth.userId
            const displayName =
              user?.displayName ??
              (isCurrentUser ? auth.displayName : undefined) ??
              userId.slice(0, 8)
            const pic = user?.picture ?? (isCurrentUser ? auth.picture : undefined)
            return {
              userId,
              displayName,
              ...(pic ? { picture: pic } : {}),
              role: (props.role as string | undefined) ?? 'member',
            }
          })
        )
      ).filter(m => m !== null)
      this.planMembers = members
    } catch {
      this.planMembers = []
    }
  }

  private async loadKnownUsersAndTeams(): Promise<void> {
    try {
      const currentId = authService.getState().userId

      await syncListOnNavigate('teams').catch(() => {})

      const allUsers = await db.users.toArray()
      this.knownUsers = allUsers.filter(u => u.id !== currentId)

      const allTeamMembers = await db.teamMembers.toArray()
      const myTeamIds = new Set(
        allTeamMembers
          .filter(tm => tm.userId === currentId && tm.status === 'active')
          .map(tm => tm.teamId)
      )
      const teams = await db.teams.toArray()
      const myTeams = teams.filter(t => myTeamIds.has(t.id))

      this.knownTeams = await Promise.all(
        myTeams.map(async team => {
          const allActive = allTeamMembers.filter(
            tm => tm.teamId === team.id && tm.status === 'active'
          )
          const otherMembers = allActive.filter(tm => tm.userId !== currentId)
          const users = (await Promise.all(otherMembers.map(tm => db.users.get(tm.userId)))).filter(
            (u): u is User => u !== undefined
          )
          return {
            team,
            members: users,
            memberUserIds: otherMembers.map(tm => tm.userId),
            totalMembers: allActive.length,
          }
        })
      )
    } catch {
      // non-critical
    }
  }

  private get filteredMemberSuggestions(): Array<
    | { type: 'user'; user: User }
    | { type: 'team'; team: Team; members: User[]; memberUserIds: string[]; totalMembers: number }
  > {
    if (!this.memberInput.trim()) return []
    const q = this.memberInput.toLowerCase()
    const memberIds = new Set(this.planMembers.map(m => m.userId))

    const userResults = this.knownUsers
      .filter(u => {
        if (memberIds.has(u.id)) return false
        return u.displayName.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
      })
      .slice(0, 4)
      .map(user => ({ type: 'user' as const, user }))

    const teamResults = this.knownTeams
      .filter(t => t.team.name.toLowerCase().includes(q))
      .slice(0, 2)
      .map(t => ({
        type: 'team' as const,
        team: t.team,
        members: t.members,
        memberUserIds: t.memberUserIds,
        totalMembers: t.totalMembers,
      }))

    return [...teamResults, ...userResults]
  }

  private async addMemberByUserId(userId: string): Promise<void> {
    if (!this.editPlanId) return
    const memberIds = new Set(this.planMembers.map(m => m.userId))
    if (memberIds.has(userId)) return
    await planStorage.addMember(this.editPlanId, userId, 'member', 'active')
    await this.loadPlanMembers()
    this.memberInput = ''
    this.showMemberSuggestions = false
    void this.loadAvailableContacts()
  }

  private async addTeamMembersToPlan(
    team: Team,
    members: User[],
    memberUserIds: string[]
  ): Promise<void> {
    if (!this.editPlanId) return
    const memberIds = new Set(this.planMembers.map(m => m.userId))
    const resolvedUserIds = new Set(members.map(u => u.id))
    const additions: Promise<void>[] = []

    for (const user of members) {
      if (!memberIds.has(user.id)) {
        additions.push(planStorage.addMember(this.editPlanId, user.id, 'member', 'active'))
      }
    }
    for (const userId of memberUserIds) {
      if (!resolvedUserIds.has(userId) && !memberIds.has(userId)) {
        additions.push(planStorage.addMember(this.editPlanId, userId, 'member', 'active'))
      }
    }

    if (additions.length > 0) {
      await Promise.all(additions)
      await this.loadPlanMembers()
    }

    if (team.emergencyContact) {
      const alreadyAdded = this.emergencyContacts.some(
        c => typeof c.source === 'object' && c.source.type === 'team' && c.source.teamId === team.id
      )
      if (!alreadyAdded) {
        this.emergencyContacts = [
          ...this.emergencyContacts,
          {
            id: crypto.randomUUID(),
            name: team.emergencyContact.name,
            phone: team.emergencyContact.phone,
            source: { type: 'team', teamId: team.id, teamName: team.name },
          },
        ]
      }
    }

    this.memberInput = ''
    this.showMemberSuggestions = false
    void this.loadAvailableContacts()
  }

  private async removePlanMember(userId: string): Promise<void> {
    if (!this.editPlanId) return
    const currentUserId = authService.getState().userId
    if (userId === currentUserId) return
    await edgeService.deleteExact(
      nodeRef('plan', this.editPlanId),
      'has-member',
      nodeRef('user', userId)
    )
    await this.loadPlanMembers()
    void this.loadAvailableContacts()
  }

  private async populateFromPlan(p: Plan): Promise<void> {
    this.name = p.name
    this.description = p.description ?? ''
    if (p.descriptionRich) {
      this.descriptionRich = p.descriptionRich as JSONContent | undefined
    } else if (p.description) {
      const { plainTextToTiptapJson } = await import('../../lib/tiptap-config.js')
      this.descriptionRich = plainTextToTiptapJson(p.description)
    }
    this.location = p.location ?? null
    this.times = [...p.times]
    this.decisionFactors = [...p.decisionFactors]
    this.decisionRules = [...p.decisionRules]
    this.objectiveHazards = p.objectiveHazards ?? ''
    if (p.objectiveHazardsRich) {
      this.objectiveHazardsRich = p.objectiveHazardsRich as JSONContent | undefined
    } else if (p.objectiveHazards) {
      const { plainTextToTiptapJson } = await import('../../lib/tiptap-config.js')
      this.objectiveHazardsRich = plainTextToTiptapJson(p.objectiveHazards)
    }
    this.emergencyContacts = Array.isArray(p.emergencyContacts) ? [...p.emergencyContacts] : []
    this.teamGear = Array.isArray(p.teamGear) ? p.teamGear : []
    this.individualGear = Array.isArray(p.individualGear) ? p.individualGear : []
  }

  private resetForm(): void {
    this.name = ''
    this.description = ''
    this.descriptionRich = undefined
    this.location = null
    this.locationOsmType = undefined
    this.locationOsmId = undefined
    this.locationIcon = undefined
    this.locationChanged = false
    this.times = [
      {
        id: crypto.randomUUID(),
        name: 'Meeting Time',
        datetime: this.getDefaultDatetime(),
        tag: 'meeting' as PlanTimeTag,
      },
    ]
    this.decisionFactors = DEFAULT_FACTORS.map(f => ({ ...f, id: crypto.randomUUID() }))
    this.decisionRules = [...DEFAULT_RULES]
    this.objectiveHazards = ''
    this.objectiveHazardsRich = undefined
    this.emergencyContacts = []
    this.teamGear = []
    this.individualGear = []
  }

  private handleLocationSelected(e: CustomEvent): void {
    const { location, coordinates, bounds, osmType, osmId, icon } = e.detail
    this.location = {
      name: location,
      coordinates: coordinates ?? undefined,
      bounds: bounds ?? undefined,
      icon: icon ?? undefined,
    }
    this.locationOsmType = osmType
    this.locationOsmId = osmId
    this.locationIcon = icon
    this.locationChanged = true
  }

  static mapsUrl(loc: PlanLocation): string {
    if (loc.coordinates)
      return `https://www.google.com/maps/search/?api=1&query=${loc.coordinates[0]},${loc.coordinates[1]}`
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.name)}`
  }

  private handleClose(): void {
    router.back()
  }

  // --- Times ---

  /** Get the date portion from the last time entry, or tomorrow */
  private getDefaultDatetime(): string {
    if (this.times.length > 0) {
      const lastTime = this.times[this.times.length - 1]
      // Extract date portion from the last entry's datetime
      const datePart = lastTime.datetime.split('T')[0]
      if (datePart) return `${datePart}T12:00`
    }
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return `${tomorrow.toISOString().split('T')[0]}T08:00`
  }

  private getIconForTag(tag: PlanTimeTag): string {
    return TIME_TAG_DEFAULTS.find(t => t.tag === tag)?.icon ?? 'schedule'
  }

  private static readonly DEFAULT_OVERDUE_NOTE =
    "Check with the other emergency contact (if applicable). If neither contact has been in touch by the 'Overdue By' time, call local rangers and/or police to notify them of the situation and share this document."

  private addTime(tag: PlanTimeTag, name?: string): void {
    const defaultName = name ?? TIME_TAG_DEFAULTS.find(t => t.tag === tag)?.name ?? 'Custom Time'
    this.times = [
      ...this.times,
      {
        id: crypto.randomUUID(),
        name: defaultName,
        datetime: this.getDefaultDatetime(),
        tag,
        ...(tag === 'overdue' ? { note: PlanForm.DEFAULT_OVERDUE_NOTE } : {}),
      },
    ]
  }

  private updateTime(index: number, field: keyof PlanTime, value: unknown): void {
    const updated = [...this.times]
    ;(updated[index] as Record<string, unknown>)[field] = value
    this.times = updated
  }

  private removeTime(index: number): void {
    this.times = this.times.filter((_, i) => i !== index)
  }

  @state() private timeLocationEditIndex: number | null = null
  @state() private timeNoteEditIndex: number | null = null

  private toggleTimeLocation(index: number): void {
    if (this.timeLocationEditIndex === index) {
      // Closing — if search field is empty, clear the location
      const search = this.shadowRoot?.querySelector('.time-location-search location-search') as
        | (HTMLElement & { value: string })
        | null
      if (search && !search.value.trim()) {
        this.clearTimeLocation(index)
      }
      this.timeLocationEditIndex = null
    } else {
      this.timeLocationEditIndex = index
      this.timeNoteEditIndex = null
    }
  }

  private toggleTimeNote(index: number): void {
    if (this.timeNoteEditIndex === index) {
      this.timeNoteEditIndex = null
    } else {
      this.timeNoteEditIndex = index
      this.timeLocationEditIndex = null
    }
  }

  private updateTimeNote(index: number, value: string): void {
    const updated = [...this.times]
    ;(updated[index] as Record<string, unknown>)['note'] = value || undefined
    this.times = updated
  }

  private setTimeLocation(index: number, e: CustomEvent): void {
    const { location, coordinates, bounds } = e.detail
    const updated = [...this.times]
    ;(updated[index] as Record<string, unknown>)['location'] = {
      name: location,
      coordinates: coordinates ?? undefined,
      bounds: bounds ?? undefined,
    }
    this.times = updated
    this.timeLocationEditIndex = null
  }

  private selectPointFeatureAsLocation(index: number, feature: Feature): void {
    const coords = (feature.geometry as GeoJSON.Point).coordinates
    const icon = feature.style?.markerIcon
    const updated = [...this.times]
    ;(updated[index] as Record<string, unknown>)['location'] = {
      name: feature.name,
      coordinates: [coords[1], coords[0]] as [number, number], // [lng, lat] → [lat, lng]
      ...(icon ? { icon } : {}),
    }
    this.times = updated
    this.timeLocationEditIndex = null
  }

  private clearTimeLocation(index: number): void {
    const updated = [...this.times]
    ;(updated[index] as Record<string, unknown>)['location'] = undefined
    this.times = updated
  }

  // --- Time drag-to-reorder ---

  private enableDrag(e: MouseEvent, index: number): void {
    const wrapper = (e.target as HTMLElement).closest('.time-entry-wrapper') as HTMLElement
    wrapper.draggable = true
    wrapper.addEventListener('dragstart', () => this.handleDragStart(index), { once: true })
  }

  private disableDrag(e: MouseEvent): void {
    const wrapper = (e.target as HTMLElement).closest('.time-entry-wrapper') as HTMLElement
    wrapper.draggable = false
  }

  private handleDragStart(index: number): void {
    this.dragIndex = index
  }

  private handleDragOver(e: DragEvent, index: number): void {
    e.preventDefault()
    this.dragOverIndex = index
  }

  private handleDragEnd(e?: DragEvent): void {
    if (
      this.dragIndex !== null &&
      this.dragOverIndex !== null &&
      this.dragIndex !== this.dragOverIndex
    ) {
      const updated = [...this.times]
      const [moved] = updated.splice(this.dragIndex, 1)
      updated.splice(this.dragOverIndex, 0, moved)
      this.times = updated
    }
    // Reset draggable on the source element
    if (e?.target) {
      const wrapper = (e.target as HTMLElement).closest<HTMLElement>('.time-entry-wrapper')
      if (wrapper) wrapper.draggable = false
    }
    this.dragIndex = null
    this.dragOverIndex = null
  }

  // --- Factor drag-to-reorder ---

  private handleFactorDragStart(index: number): void {
    this.factorDragIndex = index
  }

  private handleFactorDragOver(e: DragEvent, index: number): void {
    e.preventDefault()
    this.factorDragOverIndex = index
  }

  private handleFactorDragEnd(): void {
    if (
      this.factorDragIndex !== null &&
      this.factorDragOverIndex !== null &&
      this.factorDragIndex !== this.factorDragOverIndex
    ) {
      const updated = [...this.decisionFactors]
      const [moved] = updated.splice(this.factorDragIndex, 1)
      updated.splice(this.factorDragOverIndex, 0, moved)
      this.decisionFactors = updated
    }
    this.factorDragIndex = null
    this.factorDragOverIndex = null
  }

  // --- Decision Factors ---

  private addFactor(name: string = ''): void {
    this.decisionFactors = [
      ...this.decisionFactors,
      {
        id: crypto.randomUUID(),
        name,
        whenWillWeKnow: 'continuous',
        green: '',
        yellow: '',
        red: '',
      },
    ]
  }

  private updateFactor(index: number, field: keyof DecisionFactor, value: string): void {
    const updated = [...this.decisionFactors]
    ;(updated[index] as Record<string, unknown>)[field] = value
    this.decisionFactors = updated
  }

  private removeFactor(index: number): void {
    this.decisionFactors = this.decisionFactors.filter((_, i) => i !== index)
  }

  // --- Decision Rules ---

  private updateRule(index: number, value: string): void {
    const updated = [...this.decisionRules]
    updated[index] = value
    this.decisionRules = updated
  }

  // --- Save ---

  private get isEdit(): boolean {
    return !!this.editPlanId && !!this.existingPlan
  }

  private isFormValid(): boolean {
    return !!this.name.trim()
  }

  private async handleSave(): Promise<void> {
    try {
      const validTimes = this.times.filter(t => t.name.trim() && t.datetime)
      const factors = this.decisionFactors.filter(f => f.name.trim())
      const rules = this.decisionRules.filter(r => r.trim())
      if (this.isEdit && this.existingPlan) {
        await planStorage.updatePlan(this.existingPlan.id, {
          name: this.name.trim(),
          description: this.description.trim() || undefined,
          descriptionRich: this.descriptionRich as RichText | undefined,
          location: this.location ?? undefined,
          times: validTimes,
          decisionFactors: factors,
          decisionRules: rules,
          objectiveHazards: this.objectiveHazards.trim() || undefined,
          objectiveHazardsRich: this.objectiveHazardsRich as RichText | undefined,

          emergencyContacts: this.emergencyContacts,
          teamGear: this.teamGear,
          individualGear: this.individualGear,
        })
        if (this.locationChanged) {
          void this.fetchAndSaveLocationFeature(this.existingPlan.id, authService.getState().userId)
        }
        void forcePushEntity('plan', this.existingPlan.id)
        router.navigate(`/plans/${this.existingPlan.id}`)
      } else {
        const createdFrom = this.sourceRouteId
          ? { type: 'route' as const, id: this.sourceRouteId }
          : undefined
        const result = await planStorage.createPlan({
          name: this.name.trim() || 'Untitled Plan',
          description: this.description.trim() || undefined,
          descriptionRich: this.descriptionRich as RichText | undefined,
          location: this.location ?? undefined,
          times: validTimes,
          decisionFactors: factors,
          decisionRules: rules,
          objectiveHazards: this.objectiveHazards.trim() || undefined,
          objectiveHazardsRich: this.objectiveHazardsRich as RichText | undefined,

          emergencyContacts: this.emergencyContacts,
          teamGear: this.teamGear,
          individualGear: this.individualGear,
          origin: { type: 'user-created' },
          createdFrom,
          createdBy: authService.getState().userId,
        })
        if (result.success && result.data) {
          const planId = result.data.id
          const userId = authService.getState().userId
          if (this.sourceRouteId) {
            await this.copyFeaturesFromRoute(this.sourceRouteId, planId, userId)
          }
          void this.fetchAndSaveLocationFeature(planId, userId)
          void forcePushEntity('plan', planId)
          router.navigate(`/plans/${planId}`)
        } else {
          router.navigate('/plans')
        }
      }
    } catch (error) {
      log.error('Failed to save plan:', error)
    }
  }

  private async prefillFromRoute(routeId: string): Promise<void> {
    try {
      const route = await db.routes.get(routeId)
      if (route) {
        this.name = route.name
        this.description = route.description ?? ''
      }
    } catch (error) {
      log.error('Failed to prefill from route:', error)
    }
  }

  private async copyFeaturesFromRoute(
    routeId: string,
    planId: string,
    userId: string
  ): Promise<void> {
    const features = await featureStorage.getFeaturesForEntity('route', routeId)
    for (const feature of features) {
      const {
        id: _id,
        createdAt: _ca,
        updatedAt: _ua,
        layerId: _li,
        tripIds: _ti,
        ...rest
      } = feature
      await featureStorage.createFeature(
        { ...rest, createdBy: userId },
        { entityType: 'plan', entityId: planId }
      )
    }
  }

  private async fetchAndSaveLocationFeature(planId: string, userId: string): Promise<void> {
    const coords = this.location?.coordinates
    const osmType = this.locationOsmType
    const osmId = this.locationOsmId

    if (osmType !== 'W' && osmType !== 'R') {
      if (!coords) return
      await featureStorage.createFeature(
        {
          name: this.location?.name ?? 'Location',
          type: 'point',
          geometry: { type: 'Point', coordinates: [coords[1], coords[0]] },
          tags: ['waypoint'],
          style: {
            markerType: 'circle',
            markerIcon: this.locationIcon ?? '',
            markerSize: 24,
          },
          createdBy: userId,
        },
        { entityType: 'plan', entityId: planId }
      )
      return
    }

    if (!osmId) return

    try {
      const elementType = osmType === 'W' ? 'way' : 'relation'
      const query = `[out:json];${elementType}(${osmId});out geom;`

      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 8000)

      const resp = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: `data=${encodeURIComponent(query)}`,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        signal: controller.signal,
      })
      clearTimeout(timeout)

      if (!resp.ok) return

      const data = await resp.json()
      const element = data.elements?.[0]
      if (!element) return

      const feature = overpassToGeoJSON(element)
      if (!feature) return
      const geometry = feature.geometry

      const featureType =
        geometry.type === 'Polygon' || geometry.type === 'MultiPolygon' ? 'polygon' : 'line'

      await featureStorage.createFeature(
        {
          name: this.location?.name ?? 'Location',
          type: featureType,
          geometry,
          tags: ['boundary'],
          createdBy: userId,
        },
        { entityType: 'plan', entityId: planId }
      )
    } catch {
      // Overpass fetch failed — plan still saved, just without the outline
    }
  }

  // --- Render Sections ---

  private renderBasicInfo(): unknown {
    return html`
      <div class="form-section">
        <h2 class="section-title">Basic Information</h2>

        <div class="basic-info-layout">
          <div class="basic-info-fields">
            <md-outlined-text-field
              label="Plan Name"
              .value=${this.name}
              @input=${(e: Event): void => {
                this.name = (e.target as HTMLInputElement).value
              }}
              required
            ></md-outlined-text-field>

            <location-search
              label="Location"
              .value=${this.location?.name ?? ''}
              placeholder="Search for a location or enter coordinates..."
              .options=${{ maxResults: 8, includeCoordinateParsing: true, minQueryLength: 2 }}
              @location-selected=${this.handleLocationSelected}
            ></location-search>
            ${this.location
              ? html`<a
                  class="maps-link"
                  href=${PlanForm.mapsUrl(this.location)}
                  target="_blank"
                  rel="noopener"
                  ><span class="material-icons">open_in_new</span>View in Google Maps</a
                >`
              : ''}

            <rich-text-editor
              label="Description"
              .value=${this.descriptionRich}
              @content-changed=${(e: CustomEvent<RichTextContentEvent>): void => {
                this.descriptionRich = e.detail.json
                this.description = e.detail.text
              }}
            ></rich-text-editor>
          </div>

          <div class="basic-info-map">
            <trip-map-preview
              .planId=${this.editPlanId ?? undefined}
              .location=${this.location ?? undefined}
              style="height: 100%; min-height: 200px;"
            ></trip-map-preview>
            <div
              class="map-edit-overlay ${this.canEditRoute() ? '' : 'disabled'}"
              title=${this.canEditRoute() ? 'Edit route' : 'Enter a plan name first'}
              @click=${this.navigateToPlanMap}
            >
              <span class="material-icons">edit</span>
              <span>Edit Route</span>
            </div>
          </div>
        </div>
      </div>
    `
  }

  /**
   * Whether "Edit Route" is currently clickable. In edit mode it's always
   * available. In create mode the user must enter a plan name first, so we
   * never silently create "Untitled Plan" drafts when they tap the overlay
   * while the form is still empty.
   */
  private canEditRoute(): boolean {
    if (this.editPlanId) return true
    return this.name.trim().length > 0
  }

  /**
   * Guard against double-tap / re-entry: once we've created a draft plan
   * for this form instance we reuse its id instead of creating another.
   * A fresh mount (navigating back to /plans/new from anywhere) starts at
   * undefined, but the replaceState dance below means back-button traffic
   * returns to /plans/{draft}/edit — an edit-mode form that already has
   * editPlanId set, so it never re-enters this path.
   */
  private draftCreationInFlight = false

  private async navigateToPlanMap(): Promise<void> {
    // Editing an existing plan — go straight to the map
    if (this.editPlanId) {
      router.navigate(`/plans/${this.editPlanId}/map`)
      return
    }

    // Create mode: require a name so we never silently produce "Untitled"
    // drafts that clutter the plan list. The overlay is also visually
    // disabled in this state, but we re-check defensively.
    if (!this.canEditRoute() || this.draftCreationInFlight) {
      return
    }

    this.draftCreationInFlight = true
    try {
      const validTimes = this.times.filter(t => t.name.trim() && t.datetime)
      const factors = this.decisionFactors.filter(f => f.name.trim())
      const rules = this.decisionRules.filter(r => r.trim())
      const result = await planStorage.createPlan({
        name: this.name.trim(),
        description: this.description.trim() || undefined,
        descriptionRich: this.descriptionRich as RichText | undefined,
        location: this.location ?? undefined,
        times: validTimes,
        decisionFactors: factors,
        decisionRules: rules,
        objectiveHazards: this.objectiveHazards.trim() || undefined,
        objectiveHazardsRich: this.objectiveHazardsRich as RichText | undefined,
        teamGear: this.teamGear,
        individualGear: this.individualGear,
        origin: { type: 'user-created' },
        createdBy: authService.getState().userId,
      })
      if (result.success && result.data) {
        const newId = result.data.id
        // Replace the /plans/new history entry with /plans/{id}/edit so the
        // browser Back button returns to the form in edit mode (with state
        // preserved by editPlanId) rather than a fresh empty form. Any
        // subsequent "Edit Route" click takes the editPlanId branch above
        // and reuses this same draft instead of creating another one.
        window.history.replaceState(null, '', `/plans/${newId}/edit`)
        router.navigate(`/plans/${newId}/map`)
      }
    } catch (error) {
      log.error('Failed to create plan draft for route editing:', error)
    } finally {
      this.draftCreationInFlight = false
    }
  }

  private handleAddTimeFromSelect(e: Event): void {
    const select = e.target as HTMLSelectElement
    const value = select.value
    if (!value) return

    if (value === 'custom') {
      this.addTime('custom', '')
    } else {
      this.addTime(value as PlanTimeTag)
    }

    // Reset select back to placeholder
    select.value = ''
    if ('reset' in select && typeof select.reset === 'function') select.reset()
  }

  private renderTimes(): unknown {
    const usedTags = new Set(this.times.map(t => t.tag))
    const availableTags = TIME_TAG_DEFAULTS.filter(t => !usedTags.has(t.tag))

    return html`
      <div class="form-section">
        <h2 class="section-title">Timeline</h2>
        <!-- Desktop: buttons -->
        <div class="add-row add-times-desktop">
          ${availableTags.map(
            t => html`
              <md-outlined-button @click=${(): void => this.addTime(t.tag)}>
                <span class="material-icons" slot="icon">${t.icon}</span>
                ${t.name}
              </md-outlined-button>
            `
          )}
          <md-text-button @click=${(): void => this.addTime('custom', '')}>
            <span class="material-icons" slot="icon">add</span>
            Custom
          </md-text-button>
        </div>

        <!-- Mobile: dropdown -->
        <div class="add-times-mobile">
          <md-outlined-select label="+ Add time" @change=${this.handleAddTimeFromSelect}>
            <md-select-option value=""><div slot="headline">+ Add time</div></md-select-option>
            ${availableTags.map(
              t => html`
                <md-select-option value=${t.tag}
                  ><div slot="headline">${t.name}</div></md-select-option
                >
              `
            )}
            <md-select-option value="custom"
              ><div slot="headline">Custom Time</div></md-select-option
            >
          </md-outlined-select>
        </div>

        ${this.times.length > 0
          ? html`<div class="time-entries-group">
              ${this.times.map(
                (time, i) => html`
                  <div
                    class="time-entry-wrapper ${this.dragOverIndex === i ? 'drag-over' : ''} ${this
                      .dragIndex === i
                      ? 'dragging'
                      : ''}"
                    @dragover=${(e: DragEvent): void => this.handleDragOver(e, i)}
                    @dragend=${(e: DragEvent): void => this.handleDragEnd(e)}
                  >
                    <div class="time-entry-controls">
                      <div class="time-entry-grip">
                        <span class="material-icons time-icon"
                          >${time.location?.icon ?? this.getIconForTag(time.tag)}</span
                        >
                        <span
                          class="material-icons drag-handle"
                          @mousedown=${(e: MouseEvent): void => this.enableDrag(e, i)}
                          @mouseup=${(e: MouseEvent): void => this.disableDrag(e)}
                          >drag_indicator</span
                        >
                      </div>
                      <div class="time-fields">
                        <md-outlined-text-field
                          label="Name"
                          .value=${time.name}
                          @input=${(e: Event): void =>
                            this.updateTime(i, 'name', (e.target as HTMLInputElement).value)}
                        ></md-outlined-text-field>
                        <md-outlined-text-field
                          type="datetime-local"
                          label="Date &amp; Time"
                          .value=${time.datetime}
                          @input=${(e: Event): void =>
                            this.updateTime(i, 'datetime', (e.target as HTMLInputElement).value)}
                        ></md-outlined-text-field>
                      </div>
                      <div class="time-entry-actions">
                        <md-icon-button
                          class="location-button"
                          @click=${(): void => this.toggleTimeLocation(i)}
                          aria-label="${time.location ? 'Change location' : 'Add location'}"
                          title="${time.location ? 'Change location' : 'Add location'}"
                        >
                          <span class="material-icons"
                            >${time.location ? 'place' : 'add_location'}</span
                          >
                        </md-icon-button>
                        <md-icon-button
                          @click=${(): void => this.toggleTimeNote(i)}
                          aria-label="${time.note ? 'Edit note' : 'Add note'}"
                          title="${time.note ? 'Edit note' : 'Add note'}"
                        >
                          <span class="material-icons"
                            >${time.note ? 'sticky_note_2' : 'note_add'}</span
                          >
                        </md-icon-button>
                        <md-icon-button
                          @click=${(): void => this.removeTime(i)}
                          aria-label="Remove time"
                        >
                          <span class="material-icons">close</span>
                        </md-icon-button>
                      </div>
                    </div>
                    ${time.location && this.timeLocationEditIndex !== i
                      ? html`
                          <div class="time-location-row">
                            <a
                              class="maps-link"
                              href=${PlanForm.mapsUrl(time.location)}
                              target="_blank"
                              rel="noopener"
                            >
                              <span class="material-icons">place</span>${time.location.name}
                            </a>
                          </div>
                        `
                      : ''}
                    ${this.timeLocationEditIndex === i
                      ? html`
                          <div class="time-location-search">
                            ${this.planPointFeatures.length > 0
                              ? html`
                                  <div class="route-point-chips">
                                    ${this.planPointFeatures.map(
                                      f => html`
                                        <button
                                          class="route-point-chip"
                                          @click=${(): void =>
                                            this.selectPointFeatureAsLocation(i, f)}
                                        >
                                          <span class="material-icons"
                                            >${f.style?.markerIcon ?? 'location_on'}</span
                                          >
                                          ${f.name}
                                        </button>
                                      `
                                    )}
                                  </div>
                                `
                              : nothing}
                            <location-search
                              label="Location for ${time.name}"
                              placeholder="Search for a location..."
                              .value=${time.location?.name ?? ''}
                              .options=${{
                                maxResults: 8,
                                includeCoordinateParsing: true,
                                minQueryLength: 2,
                              }}
                              @location-selected=${(e: CustomEvent): void =>
                                this.setTimeLocation(i, e)}
                            ></location-search>
                          </div>
                        `
                      : ''}
                    ${time.note && this.timeNoteEditIndex !== i
                      ? html`
                          <div class="time-note-row">
                            <span
                              class="material-icons"
                              style="font-size: 14px; color: var(--cairn-text-secondary-color, #64748b);"
                              >sticky_note_2</span
                            >
                            <span class="time-note-preview">${time.note}</span>
                          </div>
                        `
                      : ''}
                    ${this.timeNoteEditIndex === i
                      ? html`
                          <div class="time-note-edit">
                            <md-outlined-text-field
                              type="textarea"
                              label="Note"
                              .value=${time.note ?? ''}
                              @input=${(e: Event): void =>
                                this.updateTimeNote(i, (e.target as HTMLInputElement).value)}
                              placeholder="Add a note for this time..."
                              rows="3"
                            ></md-outlined-text-field>
                          </div>
                        `
                      : ''}
                  </div>
                `
              )}
            </div>`
          : nothing}
      </div>
    `
  }

  private renderDecisionFramework(): unknown {
    // Ensure we always have 3 rule slots (green, yellow, red)
    while (this.decisionRules.length < 3) this.decisionRules.push('')

    return html`
      <div class="form-section">
        <h2 class="section-title">Decision Framework</h2>
        <p class="section-description">
          Define go/no-go thresholds for each factor. Edit values directly in the table.
        </p>

        <div class="decision-table-wrapper">
          <table class="decision-table">
            <thead>
              <tr>
                <th class="col-drag"></th>
                <th class="col-factor">Factor</th>
                <th class="col-green">Green</th>
                <th class="col-yellow">Yellow</th>
                <th class="col-red">Red</th>
              </tr>
            </thead>
            <tbody>
              ${this.decisionFactors.map(
                (factor, i) => html`
                  <tr
                    class=${this.factorDragOverIndex === i ? 'drag-over' : ''}
                    @dragover=${(e: DragEvent): void => {
                      e.preventDefault()
                      this.handleFactorDragOver(e, i)
                    }}
                  >
                    <td>
                      <span
                        class="drag-handle material-icons"
                        draggable="true"
                        @dragstart=${(): void => this.handleFactorDragStart(i)}
                        @dragend=${(): void => this.handleFactorDragEnd()}
                        >drag_indicator</span
                      >
                    </td>
                    <td>
                      <div class="factor-cell">
                        <input
                          class="cell-input"
                          .value=${factor.name}
                          @change=${(e: Event): void =>
                            this.updateFactor(i, 'name', (e.target as HTMLInputElement).value)}
                          placeholder="Factor name"
                        />
                        <md-icon-button
                          @click=${(): void => this.removeFactor(i)}
                          aria-label="Remove factor"
                        >
                          <span class="material-icons">close</span>
                        </md-icon-button>
                      </div>
                    </td>
                    <td class="cell-green">
                      <input
                        class="cell-input"
                        .value=${factor.green}
                        @change=${(e: Event): void =>
                          this.updateFactor(i, 'green', (e.target as HTMLInputElement).value)}
                        placeholder="Go"
                      />
                    </td>
                    <td class="cell-yellow">
                      <input
                        class="cell-input"
                        .value=${factor.yellow}
                        @change=${(e: Event): void =>
                          this.updateFactor(i, 'yellow', (e.target as HTMLInputElement).value)}
                        placeholder="Caution"
                      />
                    </td>
                    <td class="cell-red">
                      <input
                        class="cell-input"
                        .value=${factor.red}
                        @change=${(e: Event): void =>
                          this.updateFactor(i, 'red', (e.target as HTMLInputElement).value)}
                        placeholder="No-go"
                      />
                    </td>
                  </tr>
                `
              )}
              <tr class="rules-row">
                <td class="add-factor-cell" colspan="2">
                  <md-icon-button @click=${(): void => this.addFactor()} aria-label="Add factor">
                    <span class="material-icons">add</span>
                  </md-icon-button>
                </td>
                <td class="cell-green">
                  <input
                    class="cell-input"
                    .value=${this.decisionRules[0] ?? ''}
                    @change=${(e: Event): void =>
                      this.updateRule(0, (e.target as HTMLInputElement).value)}
                  />
                </td>
                <td class="cell-yellow">
                  <input
                    class="cell-input"
                    .value=${this.decisionRules[1] ?? ''}
                    @change=${(e: Event): void =>
                      this.updateRule(1, (e.target as HTMLInputElement).value)}
                  />
                </td>
                <td class="cell-red">
                  <input
                    class="cell-input"
                    .value=${this.decisionRules[2] ?? ''}
                    @change=${(e: Event): void =>
                      this.updateRule(2, (e.target as HTMLInputElement).value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Objective Hazards -->
      <div class="form-section">
        <h2 class="section-title">Objective Hazards</h2>
        <rich-text-editor
          label="Objective Hazards"
          placeholder="Rockfall, crevasses, river crossings, exposure, etc."
          .value=${this.objectiveHazardsRich}
          @content-changed=${(e: CustomEvent<RichTextContentEvent>): void => {
            this.objectiveHazardsRich = e.detail.json
            this.objectiveHazards = e.detail.text
          }}
        ></rich-text-editor>
      </div>
    `
  }

  private addEmergencyContact(): void {
    this.emergencyContacts = [
      ...this.emergencyContacts,
      {
        id: crypto.randomUUID(),
        name: '',
        phone: '',
        relationship: '',
        source: 'custom',
      },
    ]
  }

  private updateEmergencyContact(
    index: number,
    field: 'name' | 'phone' | 'relationship',
    value: string
  ): void {
    const updated = [...this.emergencyContacts]
    updated[index] = { ...updated[index], [field]: value }
    this.emergencyContacts = updated
  }

  private removeEmergencyContact(index: number): void {
    this.emergencyContacts = this.emergencyContacts.filter((_, i) => i !== index)
  }

  private addAvailableContact(ac: (typeof this.availableContacts)[0]): void {
    const alreadyAdded = this.emergencyContacts.some(c => {
      const s = c.source
      const as = ac.contact.source
      if (typeof s === 'string' || typeof as === 'string') return false
      if (s.type === 'team' && as.type === 'team') return s.teamId === as.teamId
      if (s.type === 'member' && as.type === 'member') return s.userId === as.userId
      return false
    })
    if (alreadyAdded) return
    this.emergencyContacts = [...this.emergencyContacts, { ...ac.contact, id: crypto.randomUUID() }]
  }

  private renderMembers(): unknown {
    if (!this.isEdit) return nothing
    const currentUserId = authService.getState().userId
    const suggestions = this.filteredMemberSuggestions

    return html`
      <div class="form-section">
        <h2 class="section-title">Members (${this.planMembers.length})</h2>

        <div style="display: flex; flex-direction: column;">
          ${this.planMembers.map(m => {
            const isCurrentUser = m.userId === currentUserId
            return html`
              <div
                style="display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);"
              >
                ${m.picture
                  ? html`<img
                      src="${m.picture}"
                      alt=""
                      style="width: 36px; height: 36px; border-radius: 50%; object-fit: cover;"
                    />`
                  : html`<div
                      style="width: 36px; height: 36px; border-radius: 50%; background: var(--cairn-primary-color, #3b82f6); color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px;"
                    >
                      ${m.displayName.charAt(0).toUpperCase()}
                    </div>`}
                <div style="flex: 1; min-width: 0;">
                  <div
                    style="font-size: 14px; font-weight: 500; color: var(--cairn-text-color, #1e293b);"
                  >
                    ${m.displayName}
                    ${isCurrentUser
                      ? html`<span
                          style="font-size: 12px; color: var(--cairn-text-secondary-color, #64748b);"
                          >(you)</span
                        >`
                      : nothing}
                  </div>
                </div>
                <span
                  style="font-size: 11px; padding: 2px 8px; border-radius: 12px; background: ${m.role ===
                  'leader'
                    ? 'var(--cairn-primary-color, #3b82f6)'
                    : 'var(--cairn-border-color, #e2e8f0)'}; color: ${m.role === 'leader'
                    ? 'white'
                    : 'var(--cairn-text-secondary-color, #64748b)'};"
                  >${m.role}</span
                >
                ${!isCurrentUser
                  ? html`<md-icon-button
                      @click=${(): void => void this.removePlanMember(m.userId)}
                      aria-label="Remove member"
                    >
                      <span class="material-icons" style="font-size: 18px;">close</span>
                    </md-icon-button>`
                  : nothing}
              </div>
            `
          })}
        </div>

        <div style="position: relative; margin-top: 12px;">
          <md-outlined-text-field
            label="Search by name, email, or team"
            .value=${this.memberInput}
            @input=${(e: InputEvent): void => {
              this.memberInput = (e.target as HTMLInputElement).value
              this.showMemberSuggestions =
                this.memberInput.length > 0 && this.filteredMemberSuggestions.length > 0
            }}
            @blur=${(): void => {
              setTimeout(() => {
                this.showMemberSuggestions = false
              }, 200)
            }}
            @focus=${(): void => {
              if (this.memberInput && suggestions.length > 0) this.showMemberSuggestions = true
            }}
            style="width: 100%;"
          ></md-outlined-text-field>

          ${this.showMemberSuggestions && suggestions.length > 0
            ? html`
                <div
                  style="position: absolute; z-index: 10; top: 100%; left: 0; right: 0; background: var(--cairn-surface-color, #fff); border: 1px solid var(--cairn-border-color, #e2e8f0); border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); max-height: 200px; overflow-y: auto;"
                >
                  ${suggestions.map(s =>
                    s.type === 'team'
                      ? html`
                          <div
                            style="padding: 10px 12px; cursor: pointer; display: flex; justify-content: space-between; align-items: center;"
                            @mousedown=${(e: Event): void => {
                              e.preventDefault()
                              void this.addTeamMembersToPlan(s.team, s.members, s.memberUserIds)
                            }}
                          >
                            <span style="display: flex; align-items: center; gap: 6px;">
                              <span
                                class="material-icons"
                                style="font-size: 18px; color: var(--cairn-primary-color, #3b82f6);"
                                >groups</span
                              >
                              ${s.team.name}
                            </span>
                            <span
                              style="font-size: 12px; color: var(--cairn-text-secondary-color, #64748b);"
                              >${s.totalMembers} member${s.totalMembers !== 1 ? 's' : ''}</span
                            >
                          </div>
                        `
                      : html`
                          <div
                            style="padding: 10px 12px; cursor: pointer; display: flex; justify-content: space-between; align-items: center;"
                            @mousedown=${(e: Event): void => {
                              e.preventDefault()
                              void this.addMemberByUserId(s.user.id)
                            }}
                          >
                            <span>${s.user.displayName}</span>
                            <span
                              style="font-size: 12px; color: var(--cairn-text-secondary-color, #64748b);"
                              >${s.user.email}</span
                            >
                          </div>
                        `
                  )}
                </div>
              `
            : nothing}
        </div>
      </div>
    `
  }

  private renderEmergencyContacts(): unknown {
    const unusedContacts = this.availableContacts.filter(ac => {
      const as = ac.contact.source
      return !this.emergencyContacts.some(c => {
        const s = c.source
        if (typeof s === 'string' || typeof as === 'string') return false
        if (s.type === 'team' && as.type === 'team') return s.teamId === as.teamId
        if (s.type === 'member' && as.type === 'member') return s.userId === as.userId
        return false
      })
    })

    return html`
      <div class="form-section">
        <h2 class="section-title">Emergency Contacts</h2>
        <p class="section-description">People to contact in case of emergency during this trip.</p>

        ${unusedContacts.length > 0
          ? html`
              <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;">
                ${unusedContacts.map(
                  ac => html`
                    <button
                      style="display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 16px; border: 1px solid var(--cairn-border-color, #e2e8f0); background: var(--cairn-surface-color, #ffffff); color: var(--cairn-text-color, #1e293b); font-size: 13px; cursor: pointer;"
                      @click=${(): void => this.addAvailableContact(ac)}
                    >
                      <span
                        class="material-icons"
                        style="font-size: 16px; color: var(--cairn-primary-color, #3b82f6);"
                        >add</span
                      >
                      ${ac.label}
                      <span
                        style="color: var(--cairn-text-secondary-color, #64748b); font-size: 11px;"
                        >${ac.sublabel}</span
                      >
                    </button>
                  `
                )}
              </div>
            `
          : nothing}
        ${this.emergencyContacts.map(
          (contact, i) => html`
            <div
              style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; padding: 12px; border: 1px solid var(--cairn-border-color, #e2e8f0); border-radius: 8px;"
            >
              <div style="display: flex; align-items: center; justify-content: space-between;">
                <span style="font-size: 12px; color: var(--cairn-text-secondary-color, #64748b);">
                  ${typeof contact.source === 'string'
                    ? 'Custom'
                    : contact.source.type === 'team'
                      ? `Team: ${contact.source.teamName}`
                      : `Member: ${contact.source.displayName}`}
                </span>
                <md-icon-button
                  @click=${(): void => this.removeEmergencyContact(i)}
                  aria-label="Remove contact"
                >
                  <span class="material-icons">close</span>
                </md-icon-button>
              </div>
              <md-outlined-text-field
                label="Name"
                .value=${contact.name}
                @change=${(e: Event): void =>
                  this.updateEmergencyContact(i, 'name', (e.target as HTMLInputElement).value)}
                style="width: 100%;"
              ></md-outlined-text-field>
              <md-outlined-text-field
                label="Phone"
                type="tel"
                .value=${contact.phone}
                @change=${(e: Event): void =>
                  this.updateEmergencyContact(i, 'phone', (e.target as HTMLInputElement).value)}
                style="width: 100%;"
              ></md-outlined-text-field>
              <md-outlined-text-field
                label="Relationship"
                .value=${contact.relationship ?? ''}
                @change=${(e: Event): void =>
                  this.updateEmergencyContact(
                    i,
                    'relationship',
                    (e.target as HTMLInputElement).value
                  )}
                style="width: 100%;"
              ></md-outlined-text-field>
            </div>
          `
        )}

        <md-text-button @click=${(): void => this.addEmergencyContact()}>
          <span class="material-icons" slot="icon">add</span>
          Add Custom Contact
        </md-text-button>
      </div>
    `
  }

  private renderPackList(): unknown {
    return html`
      <div class="form-section">
        <h2 class="section-title">Pack List</h2>
        <plan-pack-editor
          .teamGear=${this.teamGear}
          .individualGear=${this.individualGear}
          @gear-changed=${(e: CustomEvent): void => {
            this.teamGear = e.detail.teamGear
            this.individualGear = e.detail.individualGear
          }}
        ></plan-pack-editor>
      </div>
    `
  }

  override render(): unknown {
    const isEditing = this.isEdit
    const title = isEditing ? `Edit ${this.existingPlan!.name}` : 'Create Plan'

    return html`
      <div class="form-container">
        <div class="form-header">
          <div class="header-left">
            <button class="back-button" @click=${this.handleClose} aria-label="Go back">
              <span class="material-icons">arrow_back</span>
            </button>
            <h1 class="form-title">${title}</h1>
          </div>
        </div>

        <div class="form-content">
          <div class="form-fields">
            ${this.renderBasicInfo()} ${this.renderMembers()} ${this.renderEmergencyContacts()}
            ${this.renderTimes()} ${this.renderPackList()} ${this.renderDecisionFramework()}

            <!-- Form Actions -->
            <div class="form-actions">
              <md-outlined-button @click=${this.handleClose}> Cancel </md-outlined-button>
              <md-filled-button @click=${this.handleSave} ?disabled=${!this.isFormValid()}>
                ${isEditing ? 'Update' : 'Create'} Plan
              </md-filled-button>
            </div>
          </div>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plan-form': PlanForm
  }
}
